import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';

interface PipelineStageData {
  count: number;
  by_currency: Record<string, number>;
}

interface DirectorStats {
  monthly_sales: { usd: number; uzs: number; count: number };
  tenders: {
    total: number;
    by_status: Record<string, number>;
    won_value: number;
  };
  warehouse: {
    value_purchase: number;
    value_sale: number;
    potential_profit: number;
  };
  receivables: {
    by_currency: Record<string, number>;
    overdue_count: number;
  };
  service: { open: number; total: number; urgent: number };
  pipeline: Record<string, PipelineStageData>;
  service_pipeline: Record<string, PipelineStageData>;
}

interface StageConfig {
  key: string;
  label: string;
  color: string;
  icon: string;
}

const pipelineStages: StageConfig[] = [
  { key: 'lead', label: 'Lead', color: '#94A3B8', icon: '🌱' },
  { key: 'technical_discussion', label: 'Texnik muhokama', color: '#60A5FA', icon: '🔧' },
  { key: 'commercial_offer', label: 'Tijorat taklifi', color: '#818CF8', icon: '📄' },
  { key: 'negotiation', label: 'Muzokara', color: '#FBBF24', icon: '🤝' },
  { key: 'tender_contract', label: 'Tender/Shartnoma', color: '#FB923C', icon: '📝' },
  { key: 'won', label: 'Yutuq', color: '#34D399', icon: '🏆' },
  { key: 'lost', label: "Yo'qotish", color: '#F87171', icon: '❌' },
];

const servicePipelineStages: StageConfig[] = [
  { key: 'new', label: 'Yangi', color: '#60A5FA', icon: '🆕' },
  { key: 'in_progress', label: 'Jarayonda', color: '#FBBF24', icon: '⚙️' },
  { key: 'waiting', label: 'Kutilmoqda', color: '#FB923C', icon: '⏳' },
  { key: 'resolved', label: 'Hal qilindi', color: '#34D399', icon: '✅' },
  { key: 'closed', label: 'Yopildi', color: '#94A3B8', icon: '🔒' },
  { key: 'cancelled', label: 'Bekor qilindi', color: '#F87171', icon: '❌' },
];

const tenderStatusLabels: Record<string, { label: string; color: string }> = {
  preparing: { label: 'Tayyorlanmoqda', color: '#60A5FA' },
  submitted: { label: 'Topshirildi', color: '#FBBF24' },
  won:       { label: 'Yutdi', color: '#34D399' },
  lost:      { label: 'Yutqazdi', color: '#F87171' },
  cancelled: { label: 'Bekor qilindi', color: '#9CA3AF' },
};

const currencySymbol: Record<string, string> = { usd: '$', eur: '€', uzs: "so'm" };

// Valyutalarni ko'rsatish tartibi: usd, uzs, eur, keyin qolganlari
const CURRENCY_ORDER = ['usd', 'uzs', 'eur'];
const sortCurrencies = (a: string, b: string) => {
  const ia = CURRENCY_ORDER.indexOf(a);
  const ib = CURRENCY_ORDER.indexOf(b);
  return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
};

const formatMoney = (amount: number, currency: string) =>
  `${amount.toLocaleString()} ${currencySymbol[currency] || currency.toUpperCase()}`;

const sumByCurrency = (data: Record<string, PipelineStageData>): Record<string, number> =>
  Object.values(data).reduce((acc, p) => {
    Object.entries(p.by_currency || {}).forEach(([cur, amt]) => {
      acc[cur] = (acc[cur] || 0) + amt;
    });
    return acc;
  }, {} as Record<string, number>);

// Bitta voronka (funnel) — Sales va Service Pipeline ikkalasi ham shu komponentdan foydalanadi
const PipelineFunnel: React.FC<{
  stages: StageConfig[];
  data: Record<string, PipelineStageData>;
  emptyLabel: string;
}> = ({ stages, data, emptyLabel }) => {
  const maxCount = Math.max(...Object.values(data).map(p => p.count), 1);
  const totalCount = Object.values(data).reduce((sum, p) => sum + p.count, 0);
  const totalByCurrency = sumByCurrency(data);

  if (totalCount === 0) {
    return <p className="text-gray-400 text-sm text-center py-6">{emptyLabel}</p>;
  }

  // Har bir qator w-40 yorliq + w-48 summa ustuniga muhtoj (~350px, faqat fixed
  // ustunlar uchun) — 380px ekranda panjaraga sig'maydi. Torraytirish o'rniga
  // (matn/foizlar o'qib bo'lmas holga kelmasligi uchun) butun voronkani
  // gorizontal skroll qilinadigan qildik, min-width bilan.
  return (
    <div className="overflow-x-auto">
      <div className="space-y-3 min-w-[480px]">
        {stages.map(stage => {
          const stageData = data[stage.key] || { count: 0, by_currency: {} };
          const widthPct = maxCount > 0 ? (stageData.count / maxCount) * 100 : 0;
          const countPct = totalCount > 0 ? Math.round((stageData.count / totalCount) * 100) : 0;
          const currencies = Object.keys(stageData.by_currency || {}).sort(sortCurrencies);

          return (
            <div key={stage.key} className="flex items-center gap-3">
              <div className="w-40 flex items-center gap-2 flex-shrink-0">
                <span>{stage.icon}</span>
                <span className="text-sm text-gray-600">{stage.label}</span>
              </div>
              <div className="flex-1 bg-gray-100 rounded-lg h-8 relative overflow-hidden">
                <div
                  className="h-8 rounded-lg transition-all flex items-center px-3"
                  style={{ width: `${Math.max(widthPct, stageData.count > 0 ? 8 : 0)}%`, backgroundColor: stage.color }}
                >
                  {stageData.count > 0 && (
                    <span className="text-white text-xs font-bold">{stageData.count} ta ({countPct}%)</span>
                  )}
                </div>
              </div>
              <div className="w-48 text-right flex-shrink-0 space-y-0.5">
                {currencies.length === 0 ? (
                  <span className="text-xs text-gray-300">—</span>
                ) : (
                  currencies.map(cur => {
                    const amt = stageData.by_currency[cur];
                    const total = totalByCurrency[cur] || 0;
                    const pct = total > 0 ? Math.round((amt / total) * 100) : 0;
                    return (
                      <div key={cur} className="text-xs text-gray-500">
                        {formatMoney(amt, cur)} ({pct}%)
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Konversiya xulosasi (masalan "Yutuq konversiyasi" yoki "Hal qilish konversiyasi")
const ConversionSummary: React.FC<{
  data: Record<string, PipelineStageData>;
  successKey: string;
  successLabel: string;
}> = ({ data, successKey, successLabel }) => {
  const totalCount = Object.values(data).reduce((sum, p) => sum + p.count, 0);
  if (totalCount === 0) return null;

  const totalByCurrency = sumByCurrency(data);
  const successData = data[successKey] || { count: 0, by_currency: {} };
  const countPct = Math.round((successData.count / totalCount) * 100);
  const currencies = Object.keys(totalByCurrency).sort(sortCurrencies);

  return (
    <div className="mt-4 pt-4 border-t space-y-1.5">
      <div className="flex justify-between items-center flex-wrap gap-x-2">
        <span className="text-sm text-gray-500">{successLabel} (soni bo'yicha)</span>
        <span className="font-bold text-green-600">{successData.count} ta ({countPct}%)</span>
      </div>
      {currencies.length === 0 ? (
        <p className="text-xs text-gray-300">Summalar mavjud emas</p>
      ) : (
        currencies.map(cur => {
          const amt = successData.by_currency[cur] || 0;
          const total = totalByCurrency[cur] || 0;
          const pct = total > 0 ? Math.round((amt / total) * 100) : 0;
          return (
            <div key={cur} className="flex justify-between items-center flex-wrap gap-x-2">
              <span className="text-sm text-gray-500">
                {successLabel} ({cur.toUpperCase()} summa bo'yicha)
              </span>
              <span className="font-bold text-green-600">
                {formatMoney(amt, cur)} ({pct}%)
              </span>
            </div>
          );
        })
      )}
    </div>
  );
};

const DirectorDashboard: React.FC = () => {
  const [stats, setStats] = useState<DirectorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Voronka filtrlari
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [assignedToId, setAssignedToId] = useState('');
  const [assignableUsers, setAssignableUsers] = useState<{id: number; full_name: string; role: string; role_label?: string}[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);

  const ROLE_GROUP_ORDER = [
    'admin','director','sales_head','sales_manager',
    'service_head','service_engineer','warehouse_head',
    'logistic_head','logist','chief_accountant','accountant',
    'hr_manager','lawyer',
  ];


  const exportPipelinePdf = (type: 'sales' | 'service') => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { jsPDF } = require('jspdf');
    const stages = type === 'sales' ? pipelineStages : servicePipelineStages;
    const data = type === 'sales' ? stats!.pipeline : stats!.service_pipeline;
    const title = type === 'sales' ? 'Sotuv voronkasi (Sales Pipeline)' : 'Servis voronkasi (Service Pipeline)';

    const totalCount = Object.values(data).reduce((s: number, p: any) => s + p.count, 0);
    const totalByCurrency: Record<string, number> = {};
    Object.values(data).forEach((p: any) => {
      Object.entries(p.by_currency || {}).forEach(([cur, amt]) => {
        totalByCurrency[cur] = (totalByCurrency[cur] || 0) + (amt as number);
      });
    });

    const d = new Date();
    const months = ['yanvar','fevral','mart','aprel','may','iyun','iyul','avgust','sentabr','oktabr','noyabr','dekabr'];
    const dateStr = d.getDate() + ' ' + months[d.getMonth()] + ' ' + d.getFullYear();

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    let y = 15;

    doc.setFillColor(0, 32, 96);
    doc.rect(0, 0, W, 28, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Lab Instruments', 14, 12);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(title, 14, 22);
    doc.setFontSize(9);
    doc.text(dateStr, W - 14, 22, { align: 'right' });

    y = 36;
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(8);
    const filterParts: string[] = [];
    if (dateFrom) filterParts.push('Dan: ' + dateFrom);
    if (dateTo) filterParts.push('Gacha: ' + dateTo);
    const filterText = filterParts.length > 0 ? filterParts.join('  |  ') : 'Barcha vaqt';

    // Mas'ul hodim nomi
    const assigneeName = assignedToId
      ? (assignableUsers.find(u => u.id.toString() === assignedToId)?.full_name || assignedToId)
      : 'Barcha hodimlar';

    doc.text('Mas\'ul: ' + assigneeName, 14, y);
    y += 5;
    doc.text('Filtr: ' + filterText + '   |   Jami: ' + totalCount + ' ta', 14, y);
    y += 10;

    const maxCount = Math.max(...Object.values(data).map((p: any) => p.count), 1);

    stages.forEach((stage: any, idx: number) => {
      const sd = (data[stage.key] as any) || { count: 0, by_currency: {} };
      const pct = totalCount > 0 ? Math.round(sd.count / totalCount * 100) : 0;
      const barW = sd.count / maxCount;

      if (idx % 2 === 0) {
        doc.setFillColor(248, 250, 252);
        doc.rect(14, y - 5, W - 28, 13, 'F');
      }

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text(stage.label, 16, y + 2);

      const barX = 66; const barMaxW = 68; const barH = 4;
      doc.setFillColor(220, 220, 220);
      doc.rect(barX, y - 1, barMaxW, barH, 'F');

      if (sd.count > 0) {
        const hex = stage.color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16) || 100;
        const g = parseInt(hex.substring(2, 4), 16) || 150;
        const b = parseInt(hex.substring(4, 6), 16) || 200;
        doc.setFillColor(r, g, b);
        doc.rect(barX, y - 1, Math.max(barMaxW * barW, 2), barH, 'F');
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(50, 50, 50);
      doc.text(sd.count + ' ta (' + pct + '%)', barX + barMaxW + 3, y + 3);

      const amounts = Object.entries(sd.by_currency || {})
        .sort((a: any, b: any) => ['usd','uzs','eur'].indexOf(a[0]) - ['usd','uzs','eur'].indexOf(b[0]))
        .map(([cur, amt]: any) => (amt as number).toLocaleString() + ' ' + cur.toUpperCase())
        .join('  ');
      if (amounts) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(80, 80, 80);
        doc.text(amounts, W - 14, y + 3, { align: 'right' });
      }
      y += 13;
    });

    y += 3;
    doc.setDrawColor(0, 32, 96);
    doc.setLineWidth(0.5);
    doc.line(14, y, W - 14, y);
    y += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 32, 96);
    doc.text('Jami summalar:', 14, y);
    y += 7;

    Object.entries(totalByCurrency)
      .sort((a: any, b: any) => ['usd','uzs','eur'].indexOf(a[0]) - ['usd','uzs','eur'].indexOf(b[0]))
      .forEach(([cur, amt]: any) => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text(cur.toUpperCase() + ':', 14, y);
        doc.setFont('helvetica', 'bold');
        doc.text((amt as number).toLocaleString(), 35, y);
        y += 7;
      });

    const pageH = doc.internal.pageSize.getHeight();
    doc.setFillColor(0, 32, 96);
    doc.rect(0, pageH - 12, W, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text('Lab Instruments MChJ  |  sales@labin.uz  |  www.labin.uz  |  +998509996600', W / 2, pageH - 5, { align: 'center' });

    const filename = (type === 'sales' ? 'sotuv' : 'servis') + '_voronka_' + d.toISOString().split('T')[0] + '.pdf';
    doc.save(filename);
  };

  const fetchStats = async (df?: string, dt?: string, uid?: string) => {
    setFilterLoading(true);
    try {
      const params: Record<string, string> = {};
      if (df) params.date_from = df;
      if (dt) params.date_to = dt;
      if (uid) params.assigned_to_id = uid;
      const res = await api.get('/dashboard/director', { params });
      setStats(res.data);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Ma\'lumotlarni yuklashda xato');
    } finally {
      setFilterLoading(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    api.get('/users/assignable').then(r => setAssignableUsers(r.data)).catch(() => {});
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center min-h-64">
      <div className="text-gray-400">Yuklanmoqda...</div>
    </div>
  );

  if (error) return (
    <div className="p-6">
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center text-red-600">
        🔒 {error}
      </div>
    </div>
  );

  if (!stats) return null;

  return (
    <div className="p-3 md:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4 md:mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-800">🏢 Direktor paneli</h2>
          <p className="text-gray-500 text-xs md:text-sm mt-1">Kompaniya bo'yicha umumiy ko'rsatkichlar</p>
        </div>
        <p className="text-xs md:text-sm text-gray-500">
          {(() => {
            const d = new Date();
            const days = ['Yakshanba','Dushanba','Seshanba','Chorshanba','Payshanba','Juma','Shanba'];
            const months = ['yanvar','fevral','mart','aprel','may','iyun','iyul','avgust','sentabr','oktabr','noyabr','dekabr'];
            return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()} yil`;
          })()}
        </p>
      </div>

      {/* Voronka filtrlari */}
      <div className="bg-white rounded-xl shadow p-3 md:p-4 mb-4 md:mb-6">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Boshlanish sanasi</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Tugash sanasi</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Mas'ul hodim</label>
            <select value={assignedToId} onChange={e => setAssignedToId(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-40">
              <option value="">— Barcha hodimlar —</option>
              {Array.from(new Set(assignableUsers.map(u => u.role)))
                .sort((a, b) => {
                  const ia = ROLE_GROUP_ORDER.indexOf(a);
                  const ib = ROLE_GROUP_ORDER.indexOf(b);
                  return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
                })
                .map(role => {
                  const groupUsers = assignableUsers.filter(u => u.role === role);
                  return (
                    <optgroup key={role} label={groupUsers[0]?.role_label || role}>
                      {groupUsers.map(u => (
                        <option key={u.id} value={u.id}>{u.full_name}</option>
                      ))}
                    </optgroup>
                  );
                })}
            </select>
          </div>
          <button onClick={() => fetchStats(dateFrom, dateTo, assignedToId)}
            disabled={filterLoading}
            className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
            {filterLoading ? '...' : '🔍 Filtrlash'}
          </button>
          {(dateFrom || dateTo || assignedToId) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); setAssignedToId(''); fetchStats(); }}
              className="px-3 py-1.5 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              ✕ Tozalash
            </button>
          )}
          {(dateFrom || dateTo || assignedToId) && (
            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
              Filtr faol
            </span>
          )}
        </div>
      </div>

      {/* Asosiy ko'rsatkichlar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow p-4 md:p-5 text-white">
          <p className="text-green-100 text-xs md:text-sm">Oylik sotuvlar (USD)</p>
          <p className="text-xl md:text-2xl font-bold mt-1">${stats.monthly_sales.usd.toLocaleString()}</p>
          <p className="text-green-100 text-xs mt-1">{stats.monthly_sales.count} ta bitim</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-xl shadow p-4 md:p-5 text-white">
          <p className="text-emerald-100 text-xs md:text-sm">Oylik sotuvlar (UZS)</p>
          <p className="text-xl md:text-2xl font-bold mt-1">{stats.monthly_sales.uzs.toLocaleString()}</p>
          <p className="text-emerald-100 text-xs mt-1">so'm</p>
        </div>
        <Link to="/tenders" className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow p-4 md:p-5 text-white hover:shadow-lg transition">
          <p className="text-orange-100 text-xs md:text-sm">🏛️ Tenderlar</p>
          <p className="text-xl md:text-2xl font-bold mt-1">{stats.tenders.total}</p>
          <p className="text-orange-100 text-xs mt-1">Yutilgan: {stats.tenders.won_value.toLocaleString()}</p>
        </Link>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow p-4 md:p-5 text-white">
          <p className="text-purple-100 text-xs md:text-sm">📦 Ombor qiymati</p>
          <p className="text-xl md:text-2xl font-bold mt-1">{stats.warehouse.value_purchase.toLocaleString()}</p>
          <p className="text-purple-100 text-xs mt-1">Potensial foyda: {stats.warehouse.potential_profit.toLocaleString()}</p>
        </div>
      </div>

      {/* Debitor qarzdorlik va Servis */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-6">
        <div className="bg-white rounded-xl shadow p-4 md:p-5">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-gray-700 text-sm md:text-base">💳 Debitor qarzdorlik</h3>
            <Link to="/accounting" className="text-blue-500 text-xs hover:underline">Buxgalteriya →</Link>
          </div>
          {Object.keys(stats.receivables.by_currency).length === 0 ? (
            <p className="text-gray-400 text-sm">Qarzdorlik yo'q</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(stats.receivables.by_currency).map(([currency, amount]) => (
                <div key={currency} className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">{currency.toUpperCase()}</span>
                  <span className="font-bold text-red-600">
                    {amount.toLocaleString()} {currencySymbol[currency] || currency}
                  </span>
                </div>
              ))}
              {stats.receivables.overdue_count > 0 && (
                <div className="mt-2 pt-2 border-t flex items-center gap-2 text-orange-600 text-xs">
                  <span>⚠️</span>
                  <span>{stats.receivables.overdue_count} ta muddati o'tgan invoice</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow p-4 md:p-5">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-gray-700 text-sm md:text-base">🔧 Servis buyurtmalari</h3>
            <Link to="/service-cases" className="text-blue-500 text-xs hover:underline">Barchasi →</Link>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <p className="text-xl md:text-2xl font-bold text-gray-800">{stats.service.total}</p>
              <p className="text-gray-400 text-xs">Jami</p>
            </div>
            <div className="text-center">
              <p className="text-xl md:text-2xl font-bold text-blue-600">{stats.service.open}</p>
              <p className="text-gray-400 text-xs">Ochiq</p>
            </div>
            <div className="text-center">
              <p className="text-xl md:text-2xl font-bold text-red-600">{stats.service.urgent}</p>
              <p className="text-gray-400 text-xs">Shoshilinch</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tenderlar holati */}
      <div className="bg-white rounded-xl shadow p-4 md:p-5 mb-4 md:mb-6">
        <h3 className="font-semibold text-gray-700 mb-4 text-sm md:text-base">🏛️ Tenderlar holati</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3">
          {Object.entries(tenderStatusLabels).map(([key, cfg]) => (
            <div key={key} className="text-center p-3 rounded-lg" style={{ backgroundColor: cfg.color + '15' }}>
              <p className="text-xl md:text-2xl font-bold" style={{ color: cfg.color }}>
                {stats.tenders.by_status[key] || 0}
              </p>
              <p className="text-gray-500 text-xs mt-1">{cfg.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Sotuv voronkasi (Sales Pipeline) */}
      <div className="bg-white rounded-xl shadow p-4 md:p-5 mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
          <h3 className="font-semibold text-gray-700 text-sm md:text-base">📊 Sotuv voronkasi (Sales Pipeline)</h3>
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => exportPipelinePdf('sales')} className="text-xs px-2 py-1 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100">📥 Yuklab olish</button>
            <Link to="/sales-quotations" className="text-blue-500 text-xs hover:underline">Tijorat takliflari →</Link>
          </div>
        </div>

        <PipelineFunnel stages={pipelineStages} data={stats.pipeline} emptyLabel="Pipeline bo'sh" />
        <ConversionSummary data={stats.pipeline} successKey="won" successLabel="Yutuq konversiyasi" />
      </div>

      {/* Servis voronkasi (Service Pipeline) */}
      <div className="bg-white rounded-xl shadow p-4 md:p-5">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
          <h3 className="font-semibold text-gray-700 text-sm md:text-base">🛠️ Servis voronkasi (Service Pipeline)</h3>
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => exportPipelinePdf('service')} className="text-xs px-2 py-1 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100">📥 Yuklab olish</button>
            <Link to="/service-cases" className="text-blue-500 text-xs hover:underline">Servis case'lar →</Link>
          </div>
        </div>

        <PipelineFunnel stages={servicePipelineStages} data={stats.service_pipeline} emptyLabel="Servis voronkasi bo'sh" />
        <ConversionSummary data={stats.service_pipeline} successKey="resolved" successLabel="Hal qilish konversiyasi" />
      </div>
    </div>
  );
};

export default DirectorDashboard;
